import { Fragment, memo, useCallback, useId, useState } from 'react';

import { type CategoryNode, CategorySelectType } from '@/api/address-book/types';
import ChevronDownIcon from '@/assets/svg/ui-elements/chevron-down.svg';
import { LockedCategoryGate, LockedCategoryMark } from '@/features/address-book/locked-category';
import {
  CATEGORY_VARIANT_COLORS,
  getAllIds,
  getCategoryVariant,
  hasOpenCategory,
} from '@/pages/address-book/utils';
import { Checkbox, Radio, RadioGroup, Typography } from '@/shared/design-system';

import {
  CardBadge,
  CardDot,
  CardHeader,
  CategoryCard,
  CategoryList,
  ChevronBox,
  DisclosureButton,
  EmptyRows,
  LeafItem,
  RadioSelection,
  RowsContainer,
} from './styled';

interface CategoryRowsProps {
  nodes: CategoryNode[];
  parentSelectType: CategoryNode['selectType'];
  depth: number;
  selectedIds: Set<string>;
  dotColor: string;
  instanceId: string;
  expandedNodeIds: Set<string>;
  onToggle: (_id: string) => void;
  onToggleExpanded: (_id: string) => void;
}

const CategoryRows = memo<CategoryRowsProps>(
  ({
    nodes,
    parentSelectType,
    depth,
    selectedIds,
    dotColor,
    instanceId,
    expandedNodeIds,
    onToggle,
    onToggleExpanded,
  }) => {
    const selectedRadio = nodes.find((node) => selectedIds.has(node.id))?.id ?? null;
    const rows = nodes.map((node) => {
      const isLeaf = node.children.length === 0;
      const isSelected = selectedIds.has(node.id);
      const canOpen = !isLeaf && hasOpenCategory([node]);
      const isExpanded = canOpen && expandedNodeIds.has(node.id);
      const childListId = `${instanceId}-${node.id}-children`;
      const labelId = `${instanceId}-${node.id}-label`;
      const count = getAllIds(node.children).filter((id) => selectedIds.has(id)).length;
      const selection =
        parentSelectType === CategorySelectType.Single ? (
          <RadioSelection
            onClickCapture={(event) => {
              if (event.target instanceof HTMLInputElement) return;
              // A radio change event does not fire when its checked option is activated, but this choice must still clear.
              event.preventDefault();
              event.stopPropagation();
              if (!node.locked) onToggle(node.id);
            }}
          >
            <LockedCategoryGate locked={node.locked}>
              <Radio value={node.id} disabled={node.locked} aria-labelledby={labelId} />
            </LockedCategoryGate>
            <Typography variant="body3" as="span" id={labelId} fontWeight={isLeaf ? 400 : 600}>
              {node.name}
            </Typography>
          </RadioSelection>
        ) : (
          <>
            <LockedCategoryGate locked={node.locked}>
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggle(node.id)}
                id={`cat-${node.id}`}
                disabled={node.locked}
              />
            </LockedCategoryGate>
            <Typography
              variant="body3"
              as="label"
              id={`cat-${node.id}-label`}
              htmlFor={`cat-${node.id}`}
              fontWeight={isLeaf ? 400 : 600}
            >
              {node.name}
            </Typography>
          </>
        );

      return (
        <Fragment key={node.id}>
          <LeafItem $selected={isSelected} $color={dotColor} $depth={depth}>
            {selection}
            {node.locked && <LockedCategoryMark />}
            {!isLeaf && (
              <LockedCategoryGate locked={!canOpen}>
                <DisclosureButton
                  type="button"
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.name}`}
                  aria-expanded={isExpanded}
                  aria-controls={childListId}
                  disabled={!canOpen}
                  onClick={() => onToggleExpanded(node.id)}
                >
                  {count > 0 && <CardBadge $color={dotColor}>{count}</CardBadge>}
                  <ChevronBox $open={isExpanded} aria-hidden>
                    <ChevronDownIcon />
                  </ChevronBox>
                </DisclosureButton>
              </LockedCategoryGate>
            )}
          </LeafItem>
          {!isLeaf && (
            <RowsContainer id={childListId} hidden={!isExpanded}>
              {isExpanded && (
                <CategoryRows
                  nodes={node.children}
                  parentSelectType={node.selectType}
                  depth={depth + 1}
                  selectedIds={selectedIds}
                  dotColor={dotColor}
                  instanceId={instanceId}
                  expandedNodeIds={expandedNodeIds}
                  onToggle={onToggle}
                  onToggleExpanded={onToggleExpanded}
                />
              )}
            </RowsContainer>
          )}
        </Fragment>
      );
    });

    return parentSelectType === CategorySelectType.Single ? (
      <RadioGroup
        value={selectedRadio}
        onValueChange={(value) => {
          if (typeof value === 'string') onToggle(value);
        }}
      >
        {rows}
      </RadioGroup>
    ) : (
      <>{rows}</>
    );
  },
);

CategoryRows.displayName = 'CategoryRows';

const countSelectedOptions = (nodes: CategoryNode[], selectedIds: Set<string>): number =>
  nodes.reduce((count, node) => {
    const descendantCount = countSelectedOptions(node.children, selectedIds);
    return count + (descendantCount > 0 ? descendantCount : Number(selectedIds.has(node.id)));
  }, 0);

interface CategoryAccordionProps {
  categories: CategoryNode[];
  selectedIds: Set<string>;
  onToggle: (_id: string) => void;
}

export const CategoryAccordion = ({
  categories,
  selectedIds,
  onToggle,
}: CategoryAccordionProps) => {
  const [expandedRootIds, setExpandedRootIds] = useState<Set<string>>(() => new Set());
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(() => new Set());
  const instanceId = useId();

  const toggleRoot = useCallback((rootId: string) => {
    setExpandedRootIds((prev) => {
      const next = new Set(prev);
      if (next.has(rootId)) next.delete(rootId);
      else next.add(rootId);
      return next;
    });
  }, []);

  const toggleExpanded = useCallback((nodeId: string) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  return (
    <CategoryList>
      {categories.map((root) => {
        const color = CATEGORY_VARIANT_COLORS[getCategoryVariant(root.name)];
        const selectedCount = countSelectedOptions(root.children, selectedIds);
        const canOpen = hasOpenCategory([root]);
        const isOpen = canOpen && expandedRootIds.has(root.id);

        return (
          <CategoryCard key={root.id}>
            <LockedCategoryGate locked={!canOpen} fullWidth>
              <CardHeader
                type="button"
                aria-expanded={isOpen}
                disabled={!canOpen}
                onClick={() => toggleRoot(root.id)}
              >
                <CardDot $color={color} />
                <Typography variant="body2" fontWeight={600}>
                  {root.name}
                </Typography>
                {root.locked && <LockedCategoryMark />}
                {selectedCount > 0 && <CardBadge $color={color}>{selectedCount}</CardBadge>}
                <ChevronBox $open={isOpen} aria-hidden>
                  <ChevronDownIcon />
                </ChevronBox>
              </CardHeader>
            </LockedCategoryGate>

            {isOpen && (
              <RowsContainer>
                {root.children.length === 0 ? (
                  <EmptyRows>
                    <Typography variant="body3">No subcategories available.</Typography>
                  </EmptyRows>
                ) : (
                  <CategoryRows
                    nodes={root.children}
                    parentSelectType={root.selectType}
                    depth={0}
                    selectedIds={selectedIds}
                    dotColor={color}
                    instanceId={instanceId}
                    expandedNodeIds={expandedNodeIds}
                    onToggle={onToggle}
                    onToggleExpanded={toggleExpanded}
                  />
                )}
              </RowsContainer>
            )}
          </CategoryCard>
        );
      })}
    </CategoryList>
  );
};
